import { friendlyCrmSyncError } from "@/lib/crmSyncError";
import type { LeadSummary } from "@/types/entities";

type Tone = "neutral" | "warning" | "danger" | "info";

export interface EmptyAction {
  label: string;
  /** In-app route; omitted for the retrigger action. */
  to?: string;
  retrigger?: boolean;
}

export interface EmptyReason {
  label: string;
  tone: Tone;
  /** One-line explanation of why the cell is empty. */
  tooltip: string;
  /** Concrete things the user can do to get this cell filled. */
  steps?: string[];
  actions?: EmptyAction[];
}

const RETRIGGER: EmptyAction = { label: "Retrigger lead", retrigger: true };
const CHANNELS: EmptyAction = { label: "Set up channels", to: "/integrations" };
const LEADS_SETTINGS: EmptyAction = { label: "Open leads settings", to: "/settings" };

function enquirerPhones(row: LeadSummary): string[] {
  return [row.enquirerMobilePhone, row.enquirerHomePhone]
    .map((p) => p?.trim() ?? "")
    .filter(Boolean);
}

/** Maps a backend reachout error onto the setting the user has to change. */
function reachoutErrorActions(error: string): EmptyAction[] {
  const text = error.toLowerCase();
  if (text.includes("no active communication channel")) return [CHANNELS, RETRIGGER];
  if (text.includes("lead processing disabled")) return [LEADS_SETTINGS, RETRIGGER];
  return [RETRIGGER];
}

function reachoutErrorSteps(error: string): string[] {
  const text = error.toLowerCase();
  if (text.includes("no active communication channel")) {
    return ["Connect and activate a WhatsApp, SMS or email channel for this branch.", "Then retrigger the lead."];
  }
  if (text.includes("lead processing disabled")) {
    return ["Turn lead processing back on for this enquiry type and branch.", "Then retrigger the lead."];
  }
  return ["Check the reason above, fix it, then retrigger the lead."];
}

export function propertyEmptyReason(): EmptyReason {
  return {
    label: "Not specified",
    tone: "neutral",
    tooltip: "The enquirer didn't mention a property.",
    steps: ["Open the conversation to see whether they've since named one."],
  };
}

export function contactEmptyReason(row: LeadSummary): EmptyReason | null {
  const phones = enquirerPhones(row);
  const reachoutError = row.reachoutError?.trim();
  const syncStatus = row.crmSyncStatus?.trim().toUpperCase();

  const blocked = row.contactBlockedReason;

  if (blocked === "INVALID_PHONE") {
    return {
      label: "Invalid phone",
      tone: "warning",
      tooltip: `No contact was created because the phone number is invalid${phones.length ? ` (${phones.join(", ")})` : ""}.`,
      steps: [
        "Correct the number in your CRM, in international format such as +447700900123.",
        "Then retrigger the lead.",
      ],
      actions: [RETRIGGER],
    };
  }
  if (blocked === "NO_ACTIVE_CHANNEL") {
    return {
      label: "No channel",
      tone: "warning",
      tooltip: "No contact was created because no WhatsApp, SMS or email channel is active for this branch.",
      steps: reachoutErrorSteps("no active communication channel"),
      actions: reachoutErrorActions("no active communication channel"),
    };
  }
  if (blocked === "PROCESSING_DISABLED") {
    return {
      label: "Processing off",
      tone: "neutral",
      tooltip: "No contact was created because lead processing is disabled for this enquiry type and branch.",
      steps: reachoutErrorSteps("lead processing disabled"),
      actions: reachoutErrorActions("lead processing disabled"),
    };
  }
  if (syncStatus === "FAILED") {
    return {
      label: "Sync failed",
      tone: "danger",
      tooltip: friendlyCrmSyncError(row.crmSyncError),
      steps: ["Check your CRM connection is still active.", "Then retrigger the lead to sync again."],
      actions: [{ label: "Check integrations", to: "/integrations" }, RETRIGGER],
    };
  }
  if (row.contactRefId || syncStatus === "IN_PROGRESS" || syncStatus === "CHECKPOINT") {
    return {
      label: "Syncing…",
      tone: "info",
      tooltip: "The contact is waiting to be synced to the CRM.",
      steps: ["This usually clears within a few minutes. Refresh the page to check."],
    };
  }
  if (reachoutError) {
    return {
      label: "No contact",
      tone: "neutral",
      tooltip: reachoutError,
      steps: reachoutErrorSteps(reachoutError),
      actions: reachoutErrorActions(reachoutError),
    };
  }
  if (phones.length === 0) {
    return {
      label: "No phone",
      tone: "warning",
      tooltip: "The enquirer gave no phone number.",
      steps: ["Add a phone number to the lead in your CRM.", "Then retrigger the lead."],
      actions: [RETRIGGER],
    };
  }
  return null;
}

export function conversationEmptyReason(row: LeadSummary): EmptyReason | null {
  const reachoutError = row.reachoutError?.trim();
  if (row.contactBlockedReason === "INVALID_PHONE") {
    return {
      label: "Fallback email",
      tone: "neutral",
      tooltip: "Reached out by email because the phone number was invalid, so there is no chat.",
      steps: ["Correct the phone number in your CRM, then retrigger to start a chat."],
      actions: [RETRIGGER],
    };
  }
  if (row.reachoutTriggered) {
    return {
      label: "Awaiting reply",
      tone: "info",
      tooltip: "Outreach was sent. The conversation link is saved once the enquirer replies.",
      steps: ["Nothing to do. The link appears here when they reply."],
    };
  }
  if (reachoutError) {
    return {
      label: "Outreach blocked",
      tone: "warning",
      tooltip: reachoutError,
      steps: reachoutErrorSteps(reachoutError),
      actions: reachoutErrorActions(reachoutError),
    };
  }
  return {
    label: "Pending outreach",
    tone: "neutral",
    tooltip: "No outreach has been sent yet.",
    steps: ["Outreach starts automatically. If it doesn't, retrigger the lead."],
    actions: [RETRIGGER],
  };
}
